import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useRunAndCalibration } from '../../../../hooks/useRuns';
import RunTraverseSelector from './RunTraverseSelector';
import WorkPowerTable from './WorkPowerTable';
import RawCrrTable from './RawCrrTable';
import { RunOption } from './breakdownTypes';
import { ensureUtcIso } from '../../../../utils/sessionTime';
import {
  computeAlpha,
  computeAlphaBase,
  computeGravitational,
  computeKinetic,
  computeReverseNormalisation,
  computeRider,
  computeRolling,
  integrate,
  sampleAt,
} from './breakdownMath';
import KineticSection from './sections/KineticSection';
import GravitationalSection from './sections/GravitationalSection';
import RollingSection from './sections/RollingSection';
import RiderSection from './sections/RiderSection';
import AlphaSection from './sections/AlphaSection';
import AeroFromCdaSection from './sections/AeroFromCdaSection';
import RollingPowerSection from './sections/RollingPowerSection';
import NormalisationContour from './NormalisationContour';
import NormalisationContourBody from './NormalisationContourBody';
import NormalisationSummary from './NormalisationSummary';
import CrrSummary from './CrrSummary';
import { DEFAULT_ANGLE_REF } from '../../../../constants/normalisation';

// Conventional TT-bike CdA fallback when `config.cda_ref` is not set on the calibration.
// Mirrors `DEFAULT_CONSTS.cda_ref` on the Python side; the user should typically configure
// `cda_ref` per session-config so this default is rarely hit.
const DEFAULT_CDA_REF = 0.22;

const COMPONENT_DESCRIPTIONS = {
  Rider: 'power meter measurement',
  Kinetic: 'change in kinetic energy from acceleration',
  Gravitational: 'work against road gradient',
  Rolling: 'resistance from tyres and vibration',
} as const;

function parseRunStartSeconds(run: any): number {
  const ts = run?.run_ts;
  if (!ts) return 0;
  if (typeof ts === 'number') {
    return ts > 1e12 ? ts / 1000 : ts; // accept either ms or s
  }
  // Normalise naive ISO strings to UTC so the parsed instant lines up with the
  // true-UTC grid timestamps this value is subtracted from (a bare `new Date`
  // would parse a naive string in the viewer's browser zone).
  const parsed = new Date(ensureUtcIso(ts)).getTime();
  return Number.isFinite(parsed) ? parsed / 1000 : 0;
}


export default function SessionBreakdown(): ReactElement {
  const { session } = useOutletContext<{ session: any }>();

  const runOptions: RunOption[] = useMemo(() => {
    const configs: any[] = session?.configs ?? [];
    const flattened: RunOption[] = [];
    configs.forEach((config) => {
      const configLetter = config?.letter;
      const configDescription = config?.description || config?.name || config?.label || '';
      (config?.runs ?? []).forEach((r: any) => {
        const id = r.id ?? r._id ?? r.run_id;
        if (!id) return;
        const runNumber = r.run_number;
        const parts: string[] = [];
        parts.push(runNumber != null ? `Run ${runNumber}` : `Run ${id}`);
        if (configLetter) parts.push(`Config ${configLetter}`);
        if (configDescription) parts.push(configDescription);
        flattened.push({ id, label: parts.join(' - ') });
      });
    });
    return flattened;
  }, [session]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedRunId, setSelectedRunIdState] = useState<string | null>(() => {
    return searchParams.get('run');
  });
  const [selectedTraverse, setSelectedTraverseState] = useState<number>(() => {
    const t = parseInt(searchParams.get('traverse') ?? '', 10);
    return Number.isFinite(t) && t >= 0 ? t : 0;
  });
  const [selectedCalc, setSelectedCalc] = useState<'cda' | 'crr'>(() => {
    const c = searchParams.get('calc');
    return c === 'crr' ? 'crr' : 'cda';
  });

  const setSelectedRunId = (id: string | null) => {
    setSelectedRunIdState(id);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('run', id);
        else next.delete('run');
        next.set('traverse', '0');
        return next;
      },
      { replace: true },
    );
  };

  const setSelectedTraverse = (i: number) => {
    setSelectedTraverseState(i);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('traverse', String(i));
        return next;
      },
      { replace: true },
    );
  };

  const updateSelectedCalc = (c: 'cda' | 'crr') => {
    setSelectedCalc(c);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (c === 'cda') next.delete('calc');
        else next.set('calc', c);
        return next;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (!selectedRunId && runOptions.length > 0) {
      const urlRun = searchParams.get('run');
      const matched = urlRun && runOptions.find((r) => r.id === urlRun);
      const initial = matched ? urlRun : runOptions[0].id;
      setSelectedRunIdState(initial);
      if (!matched && urlRun !== initial) {
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('run', initial);
            return next;
          },
          { replace: true },
        );
      }
    }
  }, [runOptions, selectedRunId, searchParams, setSearchParams]);

  const { data, isPending, isError, error } = useRunAndCalibration(selectedRunId ?? '');
  const run = data?.run;
  const calibration = data?.calibration;

  // The run fetched via scope=grid_data_basic_plot is heavy on gridded data but light on
  // per-traverse aggregates (`run.traverse`). The session-level run (loaded with
  // scope=calculated_data_internal) carries the per-traverse channel arrays. Look it up.
  const sessionRun = useMemo(() => {
    if (!selectedRunId || !session?.configs) return null;
    for (const config of session.configs) {
      for (const r of config?.runs ?? []) {
        const id = r.id ?? r._id ?? r.run_id;
        if (id === selectedRunId) return r;
      }
    }
    return null;
  }, [session, selectedRunId]);

  const traverseCount = useMemo(() => {
    const gateStarts: number[] | undefined = run?.data_static?.gate?.GatesStartTs_tG3C;
    return gateStarts?.length ?? 0;
  }, [run]);

  useEffect(() => {
    if (traverseCount > 0 && selectedTraverse >= traverseCount) {
      setSelectedTraverse(0);
    }
  }, [traverseCount, selectedTraverse]);

  const breakdown = useMemo(() => {
    if (!run || traverseCount === 0 || selectedTraverse >= traverseCount) return null;

    const gridded = run.data_gridded ?? {};
    const ts: number[] = gridded.TimeRel_tG00 ?? [];
    const riderPower: number[] = gridded.RiderPwr_P ?? [];
    const dynPresG21: number[] = gridded.DynPres_pG21 ?? [];
    const dynPresG31: number[] = gridded.DynPres_pG31 ?? [];
    // Prefer pG31 (cp_corr already applied by the pipeline at processing time) so the client's
    // Alpha matches Alpha_wG3 even if run.cp_corr_calc has drifted since processing. Fall back
    // to pG21 with the live cp_corr if pG31 is absent (older payloads).
    const dynPresAlphaSource: 'pG31' | 'pG21' = dynPresG31.length === dynPresG21.length
      && dynPresG31.length > 0 ? 'pG31' : 'pG21';
    const dynPres: number[] = dynPresG21;
    const whlRevSpd: number[] = gridded.WhlRspd_n ?? [];
    const airYaw: number[] = gridded.AirYaw_AG31 ?? [];
    const airDensity: number[] = gridded.AirDensity_dG31 ?? [];
    // run.traverse comes from the session-level run (scope=calculated_data_internal), not the
    // fetched run, since scope=grid_data_basic_plot doesn't include per-traverse aggregates.
    const traverseChannels = sessionRun?.traverse ?? run.traverse ?? {};
    const elevDiffArr: number[] = traverseChannels.ElevDiff_sG3 ?? [];
    const crrG5Arr: number[] = traverseChannels.CrrInput_xG5 ?? [];
    const chainEffArr: number[] = traverseChannels.ChainEfficiency_xG3 ?? [];
    // Pipeline reference values — used to display side-by-side comparisons in the section
    const riderPipelineArr: number[] = traverseChannels.Rider_PG3 ?? [];
    const kineticPipelineArr: number[] = traverseChannels.Kinetic_PG3 ?? [];
    const alphaPipelineArr: number[] = traverseChannels.Alpha_PG3 ?? [];
    // AlphaBase_fG2 = AlphaBase_XG2 × TimeAbsDiff_tG3 — the per-gate "work-like" integral
    // (∫ DynPres_pG21 × cos(AirYaw_AG31) × WhlRspd_n dt) computed by the gridded pipeline.
    // Use this as the canonical client-side AlphaBase value; AlphaAlt = AlphaBase_fG2 × cp_corr × C.
    const alphaBasePipelineArr: number[] = traverseChannels.AlphaBase_fG2 ?? [];

    // Gate-centric channels live on data_static.gate (per the *_G3C / *_tG3C suffix)
    const gateRaw = run.data_static?.gate ?? {};
    const vStartArr: number[] = gateRaw.WhlLspdStart_vG3C ?? [];
    const vEndArr: number[] = gateRaw.WhlLspdEnd_vG3C ?? [];
    // Distance per traverse — try traverse channel first, then gate dict, then convert revs
    const distArr: number[] =
      traverseChannels.WhlLdistDiff_sG3 ??
      gateRaw.WhlLdistDiff_sG3 ??
      (Array.isArray(traverseChannels.WhlRrevDiff_NG3)
        ? traverseChannels.WhlRrevDiff_NG3.map(
          (n: number) => n * (calibration?.wheel_circumference ?? 0),
        )
        : Array.isArray(gateRaw.WhlRrevDiff_NG3)
          ? gateRaw.WhlRrevDiff_NG3.map(
            (n: number) => n * (calibration?.wheel_circumference ?? 0),
          )
          : []);

    const gate = run.data_static?.gate ?? {};
    const startsAbs: number[] = gate.GatesStartTs_tG3C ?? [];
    const durations: number[] = gate.TimeAbsDiff_tG3C ?? [];

    const startAbs = startsAbs[selectedTraverse];
    const duration = durations[selectedTraverse] ?? 0;
    if (!Number.isFinite(startAbs) || duration <= 0) return null;

    // Resolve the gate window in TimeRel_tG00 coordinates. The gate timestamps from
    // data_static.gate are in epoch seconds; the gridded ts axis is relative seconds.
    //
    // Primary strategy: use `Gate_NG10` — the per-sample *active-traverse* counter
    // (shipped in basic_plot, 1-indexed; 0 marks samples inside inactive/excluded
    // segments). This is the same channel the run plotter uses via getTraverseLap()
    // (Plotter.tsx:417-435) to label only the kept segments. Mapping is direct:
    // `selectedTraverse + 1 === Gate_NG10[i]` finds the i-th *valid* segment, so
    // inactive segments at the start of the run are correctly skipped.
    //
    // Fallback strategy: subtract `parseRunStartSeconds(run)` from the epoch-second
    // gate timestamps. Used only for older payloads that lack Gate_NG10.
    const gateNg10: number[] = gridded.Gate_NG10 ?? [];
    let lapFirstIdx = -1;
    let lapLastIdx = -1;
    const lapTarget = selectedTraverse + 1;
    for (let i = 0; i < gateNg10.length; i++) {
      if (Math.round(gateNg10[i]) === lapTarget) {
        if (lapFirstIdx === -1) lapFirstIdx = i;
        lapLastIdx = i;
      }
    }

    let startTs: number;
    let endTs: number;
    if (
      lapFirstIdx >= 0 && lapLastIdx >= 0
      && Number.isFinite(ts[lapFirstIdx]) && Number.isFinite(ts[lapLastIdx])
    ) {
      startTs = ts[lapFirstIdx];
      endTs = ts[lapLastIdx];
    } else {
      const runStartSec = parseRunStartSeconds(run);
      startTs = startAbs - runStartSec;
      endTs = startTs + duration;
    }
    const gateBoundary = { startTs, endTs, duration };

    const combinedMass = calibration?.combined_mass ?? 0;
    const crr = calibration?.crr ?? 0;
    const wheelCircumference = calibration?.wheel_circumference ?? 0;
    // Use calibration.cp_corr (the value the pipeline actually used at processing time, per
    // calibration_const.py:124) — NOT cp_corr_calc (the algorithm's diagnostic estimate). This
    // ensures the client's AlphaAlt = AlphaBase_fG2 × cp_corr × C reconstruction matches the
    // pipeline's AlphaAlt_PG3 = AlphaBase_XG2 × md.cp_corr × wheel_circumference exactly.
    const cpCorr = calibration?.cp_corr ?? run.cp_corr_calc ?? 1;
    const powerMeterSlope = calibration?.power_meter_slope ?? 1;
    const chainEfficiency = Number.isFinite(chainEffArr[selectedTraverse])
      ? chainEffArr[selectedTraverse]
      : 1;
    // Pipeline values for the selected traverse (W → multiply by ΔT for work)
    const riderPipelinePower = Number.isFinite(riderPipelineArr[selectedTraverse])
      ? riderPipelineArr[selectedTraverse]
      : NaN;
    const kineticPipelinePower = Number.isFinite(kineticPipelineArr[selectedTraverse])
      ? kineticPipelineArr[selectedTraverse]
      : NaN;
    const alphaPipelinePower = Number.isFinite(alphaPipelineArr[selectedTraverse])
      ? alphaPipelineArr[selectedTraverse]
      : NaN;
    const alphaBasePipeline = Number.isFinite(alphaBasePipelineArr[selectedTraverse])
      ? alphaBasePipelineArr[selectedTraverse]
      : NaN;

    // Per-traverse precomputed inputs (matching the pipeline). Each falls back to a sensible
    // computation if the precomputed channel isn't in this scope of the API response.
    const crrTempAdjusted = Number.isFinite(crrG5Arr[selectedTraverse])
      ? crrG5Arr[selectedTraverse]
      : crr;
    const vStart = Number.isFinite(vStartArr[selectedTraverse])
      ? vStartArr[selectedTraverse]
      : sampleAt(ts, whlRevSpd, startTs) * wheelCircumference;
    const vEnd = Number.isFinite(vEndArr[selectedTraverse])
      ? vEndArr[selectedTraverse]
      : sampleAt(ts, whlRevSpd, endTs) * wheelCircumference;
    const traverseDistance = Number.isFinite(distArr[selectedTraverse])
      ? distArr[selectedTraverse]
      : (() => {
        const linearSpeed = whlRevSpd.map((rs) => rs * wheelCircumference);
        return integrate(ts, linearSpeed, startTs, endTs);
      })();

    const riderPipelineRaw = Number.isFinite(traverseChannels.Rider_PG2?.[selectedTraverse])
      ? traverseChannels.Rider_PG2[selectedTraverse]
      : NaN;
    const rider = computeRider(
      ts,
      riderPower,
      gateBoundary,
      powerMeterSlope,
      chainEfficiency,
      riderPipelineRaw,
    );
    const kinetic = computeKinetic(
      vStart,
      vEnd,
      gateBoundary,
      combinedMass,
      wheelCircumference,
      kineticPipelinePower,
    );
    const deltaH = Number.isFinite(elevDiffArr[selectedTraverse]) ? elevDiffArr[selectedTraverse] : 0;
    const gravitational = computeGravitational(deltaH, gateBoundary, combinedMass);
    const rolling = computeRolling(
      traverseDistance,
      gateBoundary,
      combinedMass,
      crrTempAdjusted,
      wheelCircumference,
    );
    // Alpha projects drag force into the road direction with a SINGLE cosine of yaw.
    // Pipeline reference: cda_post_processing.py:174-184 builds DynNorm = q × cos(yaw),
    // then Work_alpha = DynNorm × dist_diff. Drag magnitude is q·CdA along apparent wind;
    // its component along v_road is q·cos(yaw)·CdA, so Alpha (CdA = 1) = q × cos(yaw) × v_road.
    // The velocity used here is v_road (wheel speed) — there is no second cosine to apply.
    //
    // Source choice: prefer DynPres_pG31 (cp_corr already baked in by the pipeline at processing
    // time, matching standard_operations_vsdv.py:204) so the client doesn't reapply a cp_corr
    // that may have drifted. Fall back to pG21 × current cp_corr if pG31 isn't shipped.
    const dynPresAlpha = dynPresAlphaSource === 'pG31' ? dynPresG31 : dynPresG21;
    const dynPresAlphaCpFactor = dynPresAlphaSource === 'pG31' ? 1 : cpCorr;
    const dynPresInline = dynPresAlpha.map((p, i) => {
      const yawDeg = airYaw[i];
      if (!Number.isFinite(yawDeg)) return p;
      return p * Math.cos((yawDeg * Math.PI) / 180);
    });
    const alpha = computeAlpha(
      ts,
      dynPresInline,
      whlRevSpd,
      gateBoundary,
      dynPresAlphaCpFactor,
      wheelCircumference,
    );

    // AlphaBase derivation (becoming the new primary). Mirrors the gridded pipeline:
    // AlphaBase = ∫ DynPres_pG21 × cos(AirYaw_AG31) × WhlRspd_n dt    (no cp_corr, no C)
    // AlphaAlt  = AlphaBase × cp_corr × wheel_circumference
    const alphaBaseDetail = computeAlphaBase(
      ts,
      dynPresG21,
      airYaw,
      whlRevSpd,
      gateBoundary,
      cpCorr,
      wheelCircumference,
    );

    // Average |yaw| and air speed across the gate window for the contour marker
    const yawAbs = airYaw.map((y) => Math.abs(y));
    const yawAvg = duration > 0 && yawAbs.length === ts.length
      ? integrate(ts, yawAbs, startTs, endTs) / duration
      : null;
    const airspeedSamples =
      dynPres.length === airDensity.length && dynPres.length === ts.length
        ? dynPres.map((p, i) => {
            const dens = airDensity[i];
            if (!Number.isFinite(p) || !Number.isFinite(dens) || dens <= 0) return 0;
            return Math.sqrt((2 * Math.max(0, p) * cpCorr) / dens);
          })
        : null;
    const airspeedAvg = airspeedSamples && duration > 0
      ? integrate(ts, airspeedSamples, startTs, endTs) / duration
      : null;

    const aeroWork = rider.work + kinetic.work + gravitational.work + rolling.work;
    const aeroPower = duration > 0 ? aeroWork / duration : 0;
    const cda = alpha.work !== 0 ? aeroWork / alpha.work : NaN;

    // CdA normalisation summary inputs
    const cdaRawArr: number[] = traverseChannels.CdA_xG3 ?? [];
    const cdaRaw = Number.isFinite(cdaRawArr[selectedTraverse]) ? cdaRawArr[selectedTraverse] : NaN;
    const headPosSeries: number[] = gridded.BodyPosHead_sG21_cm ?? [];
    const chestPosSeries: number[] = gridded.BodyPosChest_sG21_cm ?? [];
    const headPosAvg = duration > 0 && headPosSeries.length === ts.length
      ? integrate(ts, headPosSeries, startTs, endTs) / duration
      : NaN;
    const chestPosAvg = duration > 0 && chestPosSeries.length === ts.length
      ? integrate(ts, chestPosSeries, startTs, endTs) / duration
      : NaN;
    const xwSlope = calibration?.xw_para_slope ?? 0;
    const speedSlope = calibration?.airspeed_slope ?? 0;
    const headSlope = calibration?.head_slope ?? 0;
    const headRef = calibration?.head_ref ?? 0;
    const chestSlope = calibration?.chest_slope ?? 0;
    const chestRef = calibration?.chest_ref ?? 0;
    const speedRef = session?.airspeed_ref_value ?? calibration?.airspeed_ref ?? 13.4;
    const angleRef = session?.xw_ref_angle ?? session?.team?.xw_ref_angle ?? DEFAULT_ANGLE_REF;
    const yawAdj = yawAvg != null ? (yawAvg - angleRef) ** 2 * xwSlope : 0;
    const speedAdj = airspeedAvg != null ? (airspeedAvg - speedRef) * speedSlope : 0;
    const headAdj = Number.isFinite(headPosAvg) && headSlope !== 0 ? (headPosAvg - headRef) * headSlope : 0;
    const chestAdj =
      Number.isFinite(chestPosAvg) && chestSlope !== 0 ? (chestPosAvg - chestRef) * chestSlope : 0;
    const cdaNormalised = Number.isFinite(cdaRaw)
      ? cdaRaw - (yawAdj + speedAdj + headAdj + chestAdj)
      : NaN;

    // ---- Crr-direction (reverse) calculation ------------------------------------------
    // Per-traverse pipeline values (`run.traverse.<channel>[selectedTraverse]` — the same
    // scalars the data table displays). Each traverse has its own airspeed / yaw / body
    // position, so the Adjusted CdA varies per traverse, and Aero = Alpha × Adjusted CdA
    // is correctly per-traverse. No client-side fallback: if a channel is missing, the
    // corresponding adjustment row will surface a missing-channel state.
    const pipelineAt = (arr: number[] | undefined, i: number): number | null => {
      const v = arr?.[i];
      return Number.isFinite(v) ? (v as number) : null;
    };
    const crrYaw = pipelineAt(traverseChannels.AirYawAbs_AG3, selectedTraverse);
    const crrAirspeed = pipelineAt(traverseChannels.AirSpeed_vG3, selectedTraverse);
    const crrChest = pipelineAt(traverseChannels.BodyPosChest_sG3_cm, selectedTraverse);
    const crrHead = pipelineAt(traverseChannels.BodyPosHead_sG3_cm, selectedTraverse);

    const cdaRefRaw: number | undefined = calibration?.cda_ref;
    const cdaRefValue = Number.isFinite(cdaRefRaw) ? (cdaRefRaw as number) : DEFAULT_CDA_REF;
    const cdaRefIsDefault = !Number.isFinite(cdaRefRaw);

    const reverseNorm = computeReverseNormalisation(
      cdaRefValue,
      { airspeed: crrAirspeed, yaw: crrYaw, chest: crrChest, head: crrHead },
      { speedRef, angleRef, chestRef, headRef },
      { speedSlope, xwSlope, chestSlope, headSlope },
    );

    // Aero = pipeline Alpha_PG3 × Adjusted CdA. No client-side fallback: if `Alpha_PG3`
    // isn't in the payload, downstream values will be NaN and the Crr view will surface
    // a missing-channel state.
    const aeroPowerCrr = Number.isFinite(alphaPipelinePower) && Number.isFinite(reverseNorm.cdaAdjusted)
      ? alphaPipelinePower * reverseNorm.cdaAdjusted
      : NaN;
    const aeroWorkCrr = Number.isFinite(aeroPowerCrr) ? aeroPowerCrr * duration : NaN;
    const alphaWorkForAero = Number.isFinite(alphaPipelinePower)
      ? alphaPipelinePower * duration
      : NaN;

    // Energy balance solved for rolling magnitude (user spec):
    //   Rolling Power = -Aero + Rider + Kinetic + Gravitational
    const rollingPowerCrr = Number.isFinite(aeroPowerCrr)
      ? -aeroPowerCrr + rider.power + kinetic.power + gravitational.power
      : NaN;
    const rollingWorkCrr = Number.isFinite(rollingPowerCrr) ? rollingPowerCrr * duration : NaN;

    // Per-traverse ambient temperature (°C). Pipeline uses Temp_TG5 (time-adjusted average).
    const ambientTemp = Number.isFinite(traverseChannels.Temp_TG5?.[selectedTraverse])
      ? traverseChannels.Temp_TG5[selectedTraverse]
      : NaN;

    // Crr values come from the pipeline directly (cdacalcterms.py:514-516):
    //   Crr_xG3 = -RollingResistanceCalc_PG3 / (m × g × WhlLspd_vG3)  — at ambient temp
    //   Crr_xG5 = crr_normalised_by_temp(Crr_xG3, Temp_TG5)           — normalised to 20°C
    // No client-side computation of either: if a channel is missing the row will surface
    // a missing-channel state instead of being silently approximated.
    const pipelineCrrInput = Number.isFinite(traverseChannels.Crr_xG3?.[selectedTraverse])
      ? traverseChannels.Crr_xG3[selectedTraverse]
      : NaN;
    const pipelineCrrAt20C = Number.isFinite(traverseChannels.Crr_xG5?.[selectedTraverse])
      ? traverseChannels.Crr_xG5[selectedTraverse]
      : NaN;

    // Pipeline rolling for comparison row (signed negative — convert to magnitude in section).
    const rollingPipelineArr: number[] =
      traverseChannels.RollingResistance_PG5
      ?? traverseChannels.Rolling_PG3
      ?? [];
    const pipelineRollingPower = Number.isFinite(rollingPipelineArr[selectedTraverse])
      ? rollingPipelineArr[selectedTraverse]
      : NaN;

    return {
      gate: gateBoundary,
      aero: { work: aeroWork, power: aeroPower },
      cda,
      distance: rolling.distance,
      rider,
      kinetic,
      gravitational,
      rolling,
      alpha,
      alphaBase: alphaBaseDetail,
      channels: { ts, riderPower, dynPres, dynPresInline, dynPresG21, airYaw, whlRevSpd },
      inputs: {
        combinedMass,
        crr,
        crrTempAdjusted,
        wheelCircumference,
        cpCorr,
        powerMeterSlope,
        chainEfficiency,
      },
      pipeline: {
        riderPower: riderPipelinePower,
        kineticPower: kineticPipelinePower,
        alphaPower: alphaPipelinePower,
        alphaBaseF: alphaBasePipeline,
      },
      alphaMeta: {
        dynPresSource: dynPresAlphaSource,
        cpFactor: dynPresAlphaCpFactor,
      },
      traverseYaw: yawAvg,
      traverseAirspeed: airspeedAvg,
      // Pull head / chest position from the per-traverse channel directly so the
      // value matches what the slope plot scatters. Falling back to the gate-
      // integrated G21 average (headPosAvg / chestPosAvg) when the per-traverse
      // entry is missing keeps the equations populated for older runs that lack
      // the G3-cm channel.
      traverseHead: (() => {
        const t = traverseChannels.BodyPosHead_sG3_cm?.[selectedTraverse];
        if (Number.isFinite(t)) return t;
        return Number.isFinite(headPosAvg) ? headPosAvg : null;
      })(),
      traverseChest: (() => {
        const t = traverseChannels.BodyPosChest_sG3_cm?.[selectedTraverse];
        if (Number.isFinite(t)) return t;
        return Number.isFinite(chestPosAvg) ? chestPosAvg : null;
      })(),
      normalisation: {
        cdaRaw,
        cdaNormalised,
        airspeed: { value: speedAdj, available: speedSlope !== 0 && airspeedAvg != null },
        yaw: { value: yawAdj, available: xwSlope !== 0 && yawAvg != null },
        chest: {
          value: chestAdj,
          available: chestSlope !== 0 && Number.isFinite(chestPosAvg),
        },
        head: {
          value: headAdj,
          available: headSlope !== 0 && Number.isFinite(headPosAvg),
        },
      },
      crr: {
        cdaRef: cdaRefValue,
        cdaRefIsDefault,
        cdaRefLabel: cdaRefIsDefault
          ? `fallback default = ${DEFAULT_CDA_REF.toFixed(3)} m²`
          : 'config.cda_ref',
        // Per-traverse pipeline values used to compute the adjustments
        crrAirspeed,
        crrYaw,
        crrChest,
        crrHead,
        airspeedAdj: reverseNorm.airspeedAdj,
        yawAdj: reverseNorm.yawAdj,
        chestAdj: reverseNorm.chestAdj,
        headAdj: reverseNorm.headAdj,
        cdaAdjusted: reverseNorm.cdaAdjusted,
        alphaPower: alphaPipelinePower,
        alphaWork: alphaWorkForAero,
        aeroPower: aeroPowerCrr,
        aeroWork: aeroWorkCrr,
        rollingPower: rollingPowerCrr,
        rollingWork: rollingWorkCrr,
        ambientTemp,
        pipelineCrrInput,
        pipelineCrrAt20C,
        pipelineRollingPower,
        airspeedAvailable: speedSlope !== 0 && crrAirspeed != null,
        yawAvailable: xwSlope !== 0 && crrYaw != null,
        chestAvailable: chestSlope !== 0 && crrChest != null,
        headAvailable: headSlope !== 0 && crrHead != null,
      },
    };
  }, [run, calibration, sessionRun, selectedTraverse, traverseCount]);

  const sessionAirspeedRef = useMemo<number | null>(() => {
    const v = session?.airspeed_ref_value ?? session?.airspeed_ref ?? null;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  }, [session]);

  if (runOptions.length === 0) {
    return (
      <div className="cr-p-8 cr-text-center cr-text-gray-500 cr-italic">
        This session has no runs to break down yet.
      </div>
    );
  }

  return (
    <div className="cr-max-w-5xl cr-mr-auto cr-p-6">
      <h2 className="font-montserrat cr-text-2xl cr-font-bold cr-text-blue-900 cr-mb-1">
        Energy Calculation Breakdown
      </h2>
      <p className="cr-text-sm cr-text-gray-500 cr-mb-6">
        Trace how the normalised CdA for a traverse is built up from rider, kinetic,
        gravitational, rolling and aerodynamic work components.
      </p>

      <RunTraverseSelector
        runs={runOptions}
        selectedRunId={selectedRunId}
        onSelectRun={(id) => {
          setSelectedRunId(id);
          setSelectedTraverse(0);
        }}
        traverseCount={traverseCount}
        selectedTraverse={selectedTraverse}
        onSelectTraverse={setSelectedTraverse}
        isRunLoading={isPending && !!selectedRunId}
      />

      <div
        className={
          'cr-flex cr-items-center cr-gap-4 cr-flex-wrap cr-mb-4 cr-text-sm '
          + 'cr-bg-gray-50 cr-rounded cr-px-3 cr-py-2'
        }
      >
        <span className="cr-font-medium cr-text-gray-700">Calculation:</span>
        {(['cda', 'crr'] as const).map((c) => (
          <label key={c} className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
            <input
              type="radio"
              name="breakdown-calc-mode"
              value={c}
              checked={selectedCalc === c}
              onChange={() => updateSelectedCalc(c)}
              className="cr-w-4 cr-h-4"
            />
            <span>{c === 'cda' ? 'CdA' : 'Crr'}</span>
          </label>
        ))}
      </div>

      {isPending && selectedRunId && (
        <div className="cr-text-gray-500 cr-italic">Loading run data…</div>
      )}
      {isError && (
        <div className="cr-text-red-600">
          Failed to load run data{error instanceof Error ? `: ${error.message}` : '.'}
        </div>
      )}
      {run && traverseCount === 0 && (
        <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-text-gray-500 cr-italic">
          This run has no traverses to break down.
        </div>
      )}
      {breakdown && selectedCalc === 'cda' && (
        <NormalisationSummary
          cdaRaw={breakdown.normalisation.cdaRaw}
          cdaNormalised={breakdown.normalisation.cdaNormalised}
          airspeedAdjustment={{
            label: 'Air speed adjustment',
            description: '(airspeed − airspeed_ref) × airspeed_slope',
            value: breakdown.normalisation.airspeed.value,
            available: breakdown.normalisation.airspeed.available,
          }}
          yawAdjustment={{
            label: 'Yaw adjustment',
            description: '(|yaw| − angle_ref)² × xw_para_slope',
            value: breakdown.normalisation.yaw.value,
            available: breakdown.normalisation.yaw.available,
          }}
          chestAdjustment={{
            label: 'Chest adjustment',
            description: '(chest_pos − chest_ref) × chest_slope',
            value: breakdown.normalisation.chest.value,
            available: breakdown.normalisation.chest.available,
          }}
          headAdjustment={{
            label: 'Head adjustment',
            description: '(head_pos − head_ref) × head_slope',
            value: breakdown.normalisation.head.value,
            available: breakdown.normalisation.head.available,
          }}
          rawCdaDetail={
            <WorkPowerTable
              duration={breakdown.gate.duration}
              distance={breakdown.distance}
              mainRows={[
                {
                  label: 'Rider',
                  description: COMPONENT_DESCRIPTIONS.Rider,
                  work: breakdown.rider.work,
                  power: breakdown.rider.power,
                  expandedContent: (
                    <RiderSection
                      detail={breakdown.rider}
                      duration={breakdown.gate.duration}
                      ts={breakdown.channels.ts}
                      riderPower={breakdown.channels.riderPower}
                      gateStart={breakdown.gate.startTs}
                      gateEnd={breakdown.gate.endTs}
                      powerMeterSlope={breakdown.inputs.powerMeterSlope}
                      chainEfficiency={breakdown.inputs.chainEfficiency}
                      pipelinePower={breakdown.pipeline.riderPower}
                    />
                  ),
                },
                {
                  label: 'Kinetic',
                  description: COMPONENT_DESCRIPTIONS.Kinetic,
                  work: breakdown.kinetic.work,
                  power: breakdown.kinetic.power,
                  expandedContent: (
                    <KineticSection
                      detail={breakdown.kinetic}
                      duration={breakdown.gate.duration}
                      combinedMass={breakdown.inputs.combinedMass}
                      wheelCircumference={breakdown.inputs.wheelCircumference}
                      pipelinePower={breakdown.pipeline.kineticPower}
                    />
                  ),
                },
                {
                  label: 'Gravitational',
                  description: COMPONENT_DESCRIPTIONS.Gravitational,
                  work: breakdown.gravitational.work,
                  power: breakdown.gravitational.power,
                  expandedContent: (
                    <GravitationalSection
                      detail={breakdown.gravitational}
                      duration={breakdown.gate.duration}
                      combinedMass={breakdown.inputs.combinedMass}
                    />
                  ),
                },
                {
                  label: 'Rolling',
                  description: COMPONENT_DESCRIPTIONS.Rolling,
                  work: breakdown.rolling.work,
                  power: breakdown.rolling.power,
                  expandedContent: (
                    <RollingSection
                      detail={breakdown.rolling}
                      duration={breakdown.gate.duration}
                      combinedMass={breakdown.inputs.combinedMass}
                      crr={breakdown.inputs.crrTempAdjusted}
                      crrCalibration={breakdown.inputs.crr}
                      wheelCircumference={breakdown.inputs.wheelCircumference}
                    />
                  ),
                },
              ]}
              aeroWork={breakdown.aero.work}
              aeroPower={breakdown.aero.power}
              alphaRow={{
                label: 'Alpha',
                description: 'power / work from wind with a CdA of 1.0',
                work: breakdown.alpha.work,
                power: breakdown.alpha.power,
                expandedContent: (
                  <AlphaSection
                    alphaBaseDetail={breakdown.alphaBase}
                    pipelineAlphaBaseF={breakdown.pipeline.alphaBaseF}
                    pipelineAlphaPower={breakdown.pipeline.alphaPower}
                    duration={breakdown.gate.duration}
                    ts={breakdown.channels.ts}
                    dynPresG21={breakdown.channels.dynPresG21}
                    airYaw={breakdown.channels.airYaw}
                    whlRevSpd={breakdown.channels.whlRevSpd}
                    cpCorr={breakdown.inputs.cpCorr}
                    wheelCircumference={breakdown.inputs.wheelCircumference}
                    gateStart={breakdown.gate.startTs}
                    gateEnd={breakdown.gate.endTs}
                  />
                ),
              }}
              cda={breakdown.cda}
            />
          }
          contourDetail={
            <NormalisationContour
              config={calibration}
              session={session}
              sessionRun={sessionRun}
              selectedTraverse={selectedTraverse}
              sessionAirspeedRef={sessionAirspeedRef}
              traverseYaw={breakdown.traverseYaw}
              traverseAirspeed={breakdown.traverseAirspeed}
              angleRef={session?.xw_ref_angle ?? session?.team?.xw_ref_angle ?? DEFAULT_ANGLE_REF}
            />
          }
          bodyContourDetail={
            <NormalisationContourBody
              config={calibration}
              session={session}
              sessionRun={sessionRun}
              selectedTraverse={selectedTraverse}
              traverseHead={breakdown.traverseHead}
              traverseChest={breakdown.traverseChest}
            />
          }
        />
      )}
      {breakdown && selectedCalc === 'crr' && (
        <CrrSummary
          cdaRef={breakdown.crr.cdaRef}
          cdaRefLabel={breakdown.crr.cdaRefLabel}
          cdaAdjusted={breakdown.crr.cdaAdjusted}
          airspeedAdjustment={{
            label: 'Air speed adjustment',
            description: '(airspeed − airspeed_ref) × airspeed_slope',
            value: breakdown.crr.airspeedAdj,
            available: breakdown.crr.airspeedAvailable,
          }}
          yawAdjustment={{
            label: 'Yaw adjustment',
            description: '(|yaw| − angle_ref)² × xw_para_slope',
            value: breakdown.crr.yawAdj,
            available: breakdown.crr.yawAvailable,
          }}
          chestAdjustment={{
            label: 'Chest adjustment',
            description: '(chest_pos − chest_ref) × chest_slope',
            value: breakdown.crr.chestAdj,
            available: breakdown.crr.chestAvailable,
          }}
          headAdjustment={{
            label: 'Head adjustment',
            description: '(head_pos − head_ref) × head_slope',
            value: breakdown.crr.headAdj,
            available: breakdown.crr.headAvailable,
          }}
          contourDetail={
            <NormalisationContour
              config={calibration}
              session={session}
              sessionRun={sessionRun}
              selectedTraverse={selectedTraverse}
              sessionAirspeedRef={sessionAirspeedRef}
              traverseYaw={breakdown.crr.crrYaw}
              traverseAirspeed={breakdown.crr.crrAirspeed}
              angleRef={session?.xw_ref_angle ?? session?.team?.xw_ref_angle ?? DEFAULT_ANGLE_REF}
            />
          }
          bodyContourDetail={
            <NormalisationContourBody
              config={calibration}
              session={session}
              sessionRun={sessionRun}
              selectedTraverse={selectedTraverse}
              traverseHead={breakdown.crr.crrHead}
              traverseChest={breakdown.crr.crrChest}
            />
          }
          rawCrrDetail={
            <RawCrrTable
              duration={breakdown.gate.duration}
              mainRows={[
                {
                  label: 'Alpha',
                  description: 'Alpha_PG3 — per-traverse Alpha power from pipeline',
                  work: breakdown.crr.alphaWork,
                  power: breakdown.crr.alphaPower,
                  expandedContent: (
                    <AlphaSection
                      alphaBaseDetail={breakdown.alphaBase}
                      pipelineAlphaBaseF={breakdown.pipeline.alphaBaseF}
                      pipelineAlphaPower={breakdown.pipeline.alphaPower}
                      duration={breakdown.gate.duration}
                      ts={breakdown.channels.ts}
                      dynPresG21={breakdown.channels.dynPresG21}
                      airYaw={breakdown.channels.airYaw}
                      whlRevSpd={breakdown.channels.whlRevSpd}
                      cpCorr={breakdown.inputs.cpCorr}
                      wheelCircumference={breakdown.inputs.wheelCircumference}
                      gateStart={breakdown.gate.startTs}
                      gateEnd={breakdown.gate.endTs}
                    />
                  ),
                },
                {
                  label: 'Aero',
                  description: 'Alpha × Adjusted CdA = Aero watts',
                  work: breakdown.crr.aeroWork,
                  power: breakdown.crr.aeroPower,
                  expandedContent: (
                    <AeroFromCdaSection
                      alphaPower={breakdown.crr.alphaPower}
                      cdaAdjusted={breakdown.crr.cdaAdjusted}
                      aeroPower={breakdown.crr.aeroPower}
                      aeroWork={breakdown.crr.aeroWork}
                      duration={breakdown.gate.duration}
                    />
                  ),
                },
                {
                  label: 'Rider',
                  description: COMPONENT_DESCRIPTIONS.Rider,
                  work: breakdown.rider.work,
                  power: breakdown.rider.power,
                  expandedContent: (
                    <RiderSection
                      detail={breakdown.rider}
                      duration={breakdown.gate.duration}
                      ts={breakdown.channels.ts}
                      riderPower={breakdown.channels.riderPower}
                      gateStart={breakdown.gate.startTs}
                      gateEnd={breakdown.gate.endTs}
                      powerMeterSlope={breakdown.inputs.powerMeterSlope}
                      chainEfficiency={breakdown.inputs.chainEfficiency}
                      pipelinePower={breakdown.pipeline.riderPower}
                    />
                  ),
                },
                {
                  label: 'Kinetic',
                  description: COMPONENT_DESCRIPTIONS.Kinetic,
                  work: breakdown.kinetic.work,
                  power: breakdown.kinetic.power,
                  expandedContent: (
                    <KineticSection
                      detail={breakdown.kinetic}
                      duration={breakdown.gate.duration}
                      combinedMass={breakdown.inputs.combinedMass}
                      wheelCircumference={breakdown.inputs.wheelCircumference}
                      pipelinePower={breakdown.pipeline.kineticPower}
                    />
                  ),
                },
                {
                  label: 'Gravitational',
                  description: COMPONENT_DESCRIPTIONS.Gravitational,
                  work: breakdown.gravitational.work,
                  power: breakdown.gravitational.power,
                  expandedContent: (
                    <GravitationalSection
                      detail={breakdown.gravitational}
                      duration={breakdown.gate.duration}
                      combinedMass={breakdown.inputs.combinedMass}
                    />
                  ),
                },
                {
                  label: 'Rolling Power',
                  description: '−Aero + Rider + Kinetic + Gravitational',
                  work: breakdown.crr.rollingWork,
                  power: breakdown.crr.rollingPower,
                  expandedContent: (
                    <RollingPowerSection
                      aeroPower={breakdown.crr.aeroPower}
                      riderPower={breakdown.rider.power}
                      kineticPower={breakdown.kinetic.power}
                      gravitationalPower={breakdown.gravitational.power}
                      rollingPower={breakdown.crr.rollingPower}
                      rollingWork={breakdown.crr.rollingWork}
                      duration={breakdown.gate.duration}
                      pipelineRollingPower={breakdown.crr.pipelineRollingPower}
                    />
                  ),
                },
              ]}
              ratioRows={[
                {
                  label: 'Crr Calc (Crr_xG3)',
                  description: 'pipeline per-traverse Crr at ambient temperature',
                  valueLabel: Number.isFinite(breakdown.crr.pipelineCrrInput)
                    ? breakdown.crr.pipelineCrrInput.toFixed(5)
                    : 'Crr_xG3 missing',
                  expandedContent: null,
                },
                {
                  label: 'Temperature (Temp_TG5)',
                  description: 'pipeline per-traverse ambient temperature',
                  valueLabel: Number.isFinite(breakdown.crr.ambientTemp)
                    ? `${breakdown.crr.ambientTemp.toFixed(1)} °C`
                    : 'Temp_TG5 missing',
                  expandedContent: null,
                },
                {
                  label: 'Crr @ 20°C (Crr_xG5)',
                  description: 'pipeline normalises Crr_xG3 to 20°C using its temp model',
                  valueLabel: Number.isFinite(breakdown.crr.pipelineCrrAt20C)
                    ? breakdown.crr.pipelineCrrAt20C.toFixed(5)
                    : 'Crr_xG5 missing',
                  expandedContent: null,
                },
              ]}
              crrAt20C={breakdown.crr.pipelineCrrAt20C}
            />
          }
          crrAt20C={breakdown.crr.pipelineCrrAt20C}
        />
      )}
    </div>
  );
}
